import { useCallback } from 'react';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { investNextStepEvents } from 'features/funnel-events/utils';
import { OnboardingFlow } from 'utils/types/navigation';
import { selectPrimaryAddressForGB } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { getMainStackRoutes, getStepsToRevisit, recordOnboardingStep } from 'features/wealth/utils/onboardingHistory';
import { selectHasNetworth, selectFundingSource, selectHasAnnualIncome, selectEmploymentStatus, selectShowMultiAccountTradingView, selectShouldAskForExtraInsuranceNumber, selectHideFundingAccountAndAutoinvestFromInvestOnboarding, } from '../reducers/selectors';
import { blockInvestOnboardingOnWeb } from '../webGate';
export const getNextScreen = (onboardingState, hasPrimaryAddress, hasAddressWithKYCStatus, taxId, shouldAskForExtraInsuranceNumber, hasStartedKYC, hasOnboardedPots, hasAnnualIncome, hasNetWorth, hasFundingSource, hasEmploymentStatus, connectedAccount, isMigrationFlow, hideFundingAccountAndAutoinvestFlow, 
// Steps after the current one that the user has already seen; shown again even when their data is complete.
stepsToRevisit = new Set()) => {
    const revisit = (...steps) => steps.some((step) => stepsToRevisit.has(step));
    if (onboardingState && onboardingState.accountType) {
        if (!isMigrationFlow) {
            if (!hasStartedKYC && !hasOnboardedPots) {
                if (!onboardingState?.screen || revisit('InvestPersonalDetails')) {
                    return {
                        name: 'InvestPersonalDetails',
                        params: {},
                    };
                }
                if ((onboardingState.screen === 'InvestAddress' ||
                    !hasPrimaryAddress ||
                    revisit('InvestAddress', 'AddressPostCodeScreen')) &&
                    !hasAddressWithKYCStatus &&
                    !hasStartedKYC &&
                    !hasOnboardedPots) {
                    if (hasPrimaryAddress) {
                        return {
                            name: 'InvestAddress',
                            params: {},
                        };
                    }
                    return {
                        name: 'AddressPostCodeScreen',
                        params: {
                            skipGetAddress: true,
                            flow: OnboardingFlow.Invest,
                        },
                    };
                }
            }
            if (!taxId || shouldAskForExtraInsuranceNumber || revisit('InvestInsurance')) {
                return {
                    name: 'InvestInsurance',
                    params: undefined,
                };
            }
            if (!hasAnnualIncome || revisit('InvestAnnualIncome')) {
                return {
                    name: 'InvestAnnualIncome',
                    params: {},
                };
            }
            if (!hasNetWorth || revisit('InvestNetWorth')) {
                return {
                    name: 'InvestNetWorth',
                    params: undefined,
                };
            }
            if (!hasFundingSource || revisit('InvestFundingSource')) {
                return {
                    name: 'InvestFundingSource',
                    params: undefined,
                };
            }
            if (!hasEmploymentStatus || revisit('InvestEmploymentStatus')) {
                return {
                    name: 'InvestEmploymentStatus',
                    params: undefined,
                };
            }
        }
        const isJisa = onboardingState?.accountType === 'JISA';
        if (isJisa) {
            if (typeof onboardingState?.hasJISAOrTrustFund !== 'boolean' || revisit('JISAExistingAccountCheck')) {
                return {
                    name: 'JISAExistingAccountCheck',
                    params: undefined,
                };
            }
            if (!onboardingState.firstName ||
                !onboardingState.lastName ||
                !onboardingState.dob ||
                revisit('JISAChildPersonalDetails')) {
                return {
                    name: 'JISAChildPersonalDetails',
                    params: {
                        hasJISAOrTrustFund: onboardingState.hasJISAOrTrustFund,
                    },
                };
            }
            if (!onboardingState.childAddress || revisit('JISAChildAddress')) {
                return {
                    name: 'JISAChildAddress',
                    params: {
                        dob: onboardingState.dob,
                        lastName: onboardingState.lastName,
                        firstName: onboardingState.firstName,
                        hasJISAOrTrustFund: onboardingState.hasJISAOrTrustFund,
                    },
                };
            }
        }
        const isISA = onboardingState?.accountType === 'ISA';
        if (isISA && (!onboardingState?.accountsHeldElsewhere || revisit('ISAExistingAccountCheck'))) {
            return {
                name: 'ISAExistingAccountCheck',
                params: undefined,
            };
        }
        if (!onboardingState?.didSignDeclaration ||
            hideFundingAccountAndAutoinvestFlow ||
            revisit('InvestBrokerRequirements')) {
            return {
                name: 'InvestBrokerRequirements',
                params: undefined,
            };
        }
        if (!onboardingState.hasJISAOrTrustFund) {
            if (!onboardingState.assetId || revisit('StartInvestReadyMadeFunds')) {
                return {
                    name: 'StartInvestReadyMadeFunds',
                    params: undefined,
                };
            }
            if (typeof onboardingState.amount?.initial !== 'number' || revisit('SetupContributionsChooseInitialAmount')) {
                return {
                    name: 'SetupContributionsChooseInitialAmount',
                    params: {
                        initialAmount: 100,
                        type: 'initial',
                    },
                };
            }
            if (onboardingState.autoinvestDayOfMonth === undefined || revisit('AutoinvestIntro')) {
                return {
                    name: 'AutoinvestIntro',
                    params: {
                        accountType: onboardingState.accountType,
                    },
                };
            }
            if (typeof onboardingState.amount?.monthly !== 'number' || revisit('SetupContributionsChooseMonthlyAmount')) {
                return {
                    name: 'SetupContributionsChooseMonthlyAmount',
                    params: {
                        initialAmount: 100,
                        type: 'monthly',
                    },
                };
            }
        }
        if (!onboardingState.fundingAccount || revisit('SelectFundingAccount', 'AutoinvestSelectFundingAccount')) {
            if (!connectedAccount) {
                return {
                    name: 'SelectFundingAccount',
                    params: undefined,
                };
            }
            return {
                name: 'AutoinvestSelectFundingAccount',
                params: {
                    accountType: onboardingState.accountType,
                    collectionDate: onboardingState.autoinvestDayOfMonth || 0,
                    amount: onboardingState?.amount?.monthly || 0,
                    allocations: [
                        {
                            assetId: onboardingState.assetId,
                            percentage: 100,
                        },
                    ],
                },
            };
        }
        return {
            name: 'InvestReviewAndConfirm',
            params: {},
        };
    }
    return {
        name: 'InvestSelectSecclAccountType',
        params: {},
    };
};
const useNavigateToNextScreen = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const navigateToNextScreen = useCallback((options) => {
        if (blockInvestOnboardingOnWeb())
            return;
        const userTaxId = store.getState().user.user.taxId;
        const hasNetWorth = selectHasNetworth(store.getState());
        const onboardingState = store.getState().invest.onboarding;
        const fundingSource = selectFundingSource(store.getState());
        const hasStartedKYC = !!store.getState().user.user.kycStartedAt;
        const hasAnnualIncome = selectHasAnnualIncome(store.getState());
        const employmentStatus = selectEmploymentStatus(store.getState());
        const primaryAddress = selectPrimaryAddressForGB(store.getState());
        const shouldAskForExtraInsuranceNumber = selectShouldAskForExtraInsuranceNumber(store.getState());
        const hideFundingAccountAndAutoinvestFlow = selectHideFundingAccountAndAutoinvestFromInvestOnboarding(store.getState());
        const { connectedAccount } = store.getState().invest;
        const potsAccount = store.getState().pots.account;
        const hasOnboardedPots = potsAccount ? store.getState().pots.account?.status !== 'NOT_STARTED' : false;
        const isMigrationFlow = selectShowMultiAccountTradingView(store.getState());
        const stackRoutes = getMainStackRoutes(navigation);
        const nextScreenObj = getNextScreen(onboardingState, !!primaryAddress, !!primaryAddress?.kycStatus, userTaxId, shouldAskForExtraInsuranceNumber, hasStartedKYC, hasOnboardedPots, hasAnnualIncome, hasNetWorth, !!fundingSource, !!employmentStatus, connectedAccount, isMigrationFlow, hideFundingAccountAndAutoinvestFlow, getStepsToRevisit('invest', stackRoutes));
        const choiceEvents = options?.choiceEvents ?? [];
        const nextStepEvent = investNextStepEvents[nextScreenObj.name];
        const funnelEvents = nextStepEvent ? [nextStepEvent, ...choiceEvents] : choiceEvents;
        if (funnelEvents.length) {
            dispatch(postFunnelEvents(funnelEvents));
        }
        recordOnboardingStep('invest', stackRoutes, nextScreenObj.name);
        navigation.navigate(nextScreenObj);
    }, []);
    return navigateToNextScreen;
};
export default useNavigateToNextScreen;
